// web/src/components/ceremony/RevealModal.tsx — the post-unlock "Glass broken"
// reveal (M3.6): the safe's contents, shown ONLY after the door opens at quorum.
//
// The plaintext handed in here was reconstructed CLIENT-SIDE by the container
// (combineAndOpen over the K decrypted shares) — it never came from a server. The
// modal makes that honest: a copy action, the availability-window countdown, an
// audit note, and explicit "plaintext is now in YOUR browser only" framing
// (content guide; wireframe 06). Token-only.
import React, { useEffect, useState } from 'react';
import { TriangleAlert, Clock, Link2 } from 'lucide-react';
import Modal from '../Modal';
import { CodeBlock } from '../ui';
import { Icon } from '../ui/Icon';

export interface RevealModalProps {
  /** Human label of the unsealed record (e.g. "Production DB master"). */
  recordName: string;
  /** The reconstructed plaintext secret — materialised in-browser only (R11). */
  plaintext: string;
  /** Epoch ms the availability window closes (the time-box countdown, R4). */
  expiresAt: number;
  /** Short audit reference for the request.unlocked event, if known. */
  auditRef?: string;
  onClose: () => void;
}

/** Format a remaining ms span as mm:ss (the canonical R4 countdown surface). */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function RevealModal({
  recordName,
  plaintext,
  expiresAt,
  auditRef,
  onClose,
}: RevealModalProps): React.JSX.Element {
  // Numeric/textual countdown, the reduced-motion-safe canonical surface
  // (motion-spec §4: no sweeping ring; a per-second numeric tick).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const remaining = formatRemaining(expiresAt - now);

  return (
    <Modal
      title={recordName}
      subtitle="Glass broken — vault unlocked"
      maxWidth={520}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Done
        </button>
      }
      onClose={onClose}
    >
      <div className="bgRevealHead">
        <span className="bgRevealWindow" aria-label={`Window expires in ${remaining}`}>
          <Icon icon={Clock} size="sm" aria-hidden="true" />
          {remaining} window
        </span>
      </div>

      <div className="bgRevealSecret">
        <CodeBlock code={plaintext} />
      </div>

      <div className="bgRevealWarn" role="alert">
        <span className="bgRevealWarnIcon" aria-hidden="true">
          <Icon icon={TriangleAlert} size="sm" />
        </span>
        <span>
          <strong className="bgRevealRotate">
            Treat this secret as exposed. If it is a credential, rotate it once the
            emergency is over.
          </strong>{' '}
          The plaintext is now in <strong>your browser only</strong>. The server
          never reconstructed it — it was recombined here from the submitted
          shards. The {remaining} window controls how long this view stays open and
          how long shards stay available to gather; it <strong>cannot</strong> reach
          into your browser and erase what you have already seen. Close this when you
          are done.
        </span>
      </div>

      <div className="bgRevealAudit">
        <Icon icon={Link2} size="xs" aria-hidden="true" />
        <span>
          Audit event: request.unlocked{auditRef ? ` · ${auditRef}` : ''} — no
          secret material was logged.
        </span>
      </div>
    </Modal>
  );
}

export default RevealModal;
